import React from 'react';
import { Box, Button, CircularProgress, Grid, InputLabel, Link, TextField, Typography } from '@mui/material';
import { simplifiedAuthStyles } from '../../styles/simplifiedAuth';
import { useTranslation } from 'react-i18next';
import { Link as BrowserLink } from 'react-router-dom';
import { IELink } from '../../../common/components/IELink';
import ReactHtmlParser from 'html-react-parser';
import { routeMappings } from '../../../constants';
import SimplifiedAlert from '../../../common/components/SimplifiedAlert';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import { ForgotPasswordLayoutProps } from '../../interfaces/SimplifiedAuthPageProps';
import { useAppSelector } from '../../../store/hook';
import { WCAGStrandardStyles } from '../../../common/styles/WCAGStandardStyles';

const ForgotPasswordLayout = ({
  value,
  helperText,
  inputChangeHandler,
  onResetPasswordClick,
  alert,
  loading,
  errorMessageForgotPasswordRef,
}: ForgotPasswordLayoutProps): React.JSX.Element => {
  const { layoutCode } = useAppSelector((state) => state.template);
  const { classes } = simplifiedAuthStyles({ layoutCode });
  const { classes: WCAGClasses } = WCAGStrandardStyles();
  const { t } = useTranslation();
  return (
    <form aria-labelledby="forgotPassword">
      <Box mb={0.5} className={`${classes.createPasswordBox}`}>
        <Typography role="heading" aria-level={1} id="forgotPassword" className={WCAGClasses.h3}>
          {t('resetPassword')}?
        </Typography>
        <Typography className={`${WCAGClasses.subtitle1} ${classes.marginTopBottom15}`}>
          {t('backTo')}{' '}
          <Link
            component={BrowserLink}
            className={classes.fontSize14}
            to={{
              pathname: routeMappings['AUTH_LOGIN'],
              search: '?from=forgotPassword',
            }}>
            <IELink>{t('signIn')}</IELink>
          </Link>
        </Typography>
        <InputLabel htmlFor="forgotPassword" className={`${classes.marginBottom5} ${classes.fontWeight600}`}>
          {t('email')}
        </InputLabel>
        <Grid item xs={12}>
          <TextField
            id="email"
            color="primary"
            required
            fullWidth
            value={value}
            error={helperText !== ''}
            helperText={
              helperText && (
                <span className={classes.helperText}>
                  <ErrorOutlineOutlined />
                  &nbsp;
                  {t(`${helperText}`)}
                </span>
              )
            }
            name="email"
            inputProps={{
              title: t('emailTitle'),
            }}
            onChange={inputChangeHandler('email')}
            onBlur={inputChangeHandler('email')}
            autoComplete="email"
            size="small"
            type="email"
          />
        </Grid>
        <Grid item xs={12}>
          {/** button for reset password */}
          <Button
            type="submit"
            variant="outlined"
            className={`${classes.buttonFullWidth} ${classes.registerBtn}`}
            color="primary"
            disabled={loading}
            aria-hidden={loading || value === '' || helperText !== ''}
            aria-disabled="false"
            onClick={onResetPasswordClick}
            aria-label={t('sendPasswordResetEmail')}>
            <div>{t('sendPasswordResetEmail')}</div>
            {loading && <CircularProgress size={25} className={classes.buttonProgress} />}
          </Button>
        </Grid>

        {alert?.message && (
          <div ref={errorMessageForgotPasswordRef} tabIndex={-1} className={classes.forgotPasswordMsg}>
            <SimplifiedAlert className={classes.alertIcon} state={alert.state} show={!!alert.message}>
              {ReactHtmlParser(alert.message)}
            </SimplifiedAlert>
          </div>
        )}
      </Box>
    </form>
  );
};

export default ForgotPasswordLayout;
